import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Link } from 'react-router';

import { AuthedRoutesEnum } from '@/core/enums';
import { DataUnitStatsItem } from '@/core/types';

import { ControlledCollapse } from '../Collapse/ControlledCollapse';
import Button from '../Button';

import Arrow from '@/icons/select-chevron.svg?react';
import CheckIcon from '@/icons/check.svg?react';

interface Props {
  isAllDataProvided: boolean;
  stats: DataUnitStatsItem | undefined;
  getInvoicesLink: (filter?: string) => string;
  isUploadingFiles: boolean;
  isProcessingFiles: boolean;
}

export default function CorrectDataToDo({
  isAllDataProvided,
  stats,
  getInvoicesLink,
  isUploadingFiles,
  isProcessingFiles,
}: Props) {
  const { t } = useTranslation();

  const hasHCE = stats?.errors?.length;
  const hasPE = stats?.warnings?.length;

  const btnContent = () => {
    if (hasHCE && !isUploadingFiles && !isProcessingFiles) {
      return (
        <Link
          className='Button !text-white'
          to={{
            pathname: AuthedRoutesEnum.Documents,
            search: getInvoicesLink('error'),
          }}
        >
          {t('Review *number* errors').replace(
            '*number*',
            String(stats.errors_count)
          )}
        </Link>
      );
    }

    if (hasPE && !isUploadingFiles && !isProcessingFiles) {
      return (
        <Link
          className='Button--outline'
          to={{
            pathname: AuthedRoutesEnum.Documents,
            search: getInvoicesLink('warning'),
          }}
        >
          {t('Review *number* notices').replace(
            '*number*',
            String(stats.warnings_count)
          )}
        </Link>
      );
    }

    if (!hasPE && !hasHCE && isAllDataProvided) {
      return null;
    }

    return <Button disabled>{t('Review errors')}</Button>;
  };

  return (
    <div data-testid='correct-data-todo-collapse'>
      <ControlledCollapse
        initialOpened={isAllDataProvided}
        className={classNames('mt-2 rounded-lg border p-3', {
          'bg-gray-70': !isAllDataProvided && !hasHCE,
          'border-[rgba(252,17,17,0.10)] bg-orange-200': hasHCE,
          'border-[rgba(13,168,107,0.06)] bg-[rgba(13,168,107,0.06)]':
            isAllDataProvided && !hasHCE,
        })}
        triggerRenderer={(isOpened, toggleCollapse) => (
          <div
            className={`flex cursor-pointer items-center justify-between gap-x-3 ${
              isOpened ? 'rounded-t-lg' : 'rounded-lg'
            }`}
            onClick={toggleCollapse}
          >
            <div className='flex flex-grow items-center justify-between font-bold'>
              <div className='flex flex-grow items-center gap-x-2'>
                <span>2. {t('Review and correct uploaded data')}</span>
                {isAllDataProvided && !hasHCE && <CheckIcon />}
              </div>
            </div>
            <Arrow
              className={classNames(
                'ProvideDataToDo_Collapse SellerPastPeriodsFiling__arrow',
                {
                  'SellerPastPeriodsFiling__arrow--active': isOpened,
                }
              )}
            />
          </div>
        )}
        contentRenderer={() => (
          <>
            {isAllDataProvided ? (
              hasHCE ? (
                <p className='pb-3'>
                  {t(
                    'Your data contains errors that need to be corrected. Invoices with errors will be excluded from filing, while those with notices will still be included but recommended to review.'
                  )}
                </p>
              ) : hasPE ? (
                <p className='pb-3'>
                  {t(
                    "You don't have any critical errors in your data, but there are some non-critical notices that are nice-to-fix. These will still be included in your filing even if won’t be corrected."
                  )}
                </p>
              ) : (
                <p>
                  {t(
                    "You don't have any critical errors or notices in uploaded data. Please double-check to ensure all necessary data has been uploaded."
                  )}
                </p>
              )
            ) : (
              <p className='pb-3'>
                {t(
                  'Once all data will be provided, you’ll have a full overview of all your transactions. You’ll need to check completeness and correctness of data and correct errors if needed.'
                )}
              </p>
            )}
            {btnContent()}
          </>
        )}
      />
    </div>
  );
}
